import type { Camera, OrthographicCamera, PerspectiveCamera } from "three";
import { Color, Plane, Raycaster, Vector2, Vector3 } from "three";
import type { IViewer, Measurement } from "@speckle/viewer";
import {
  Extension,
  CameraController,
  MeasurementEvent,
  MeasurementsExtension,
  MeasurementState,
  ObjectLayers,
} from "@speckle/viewer";
import { MeasurementType, type MeasurementData } from "@speckle/shared/viewer/state";

import { installPickAssist, uninstallPickAssist, toNdc } from "./measurementPickAssist";
import {
  alignAreaToPlane,
  collectSnapPoints,
  constrainToAreaPlane,
  isOnAreaSurface,
  findNearestScreenPoint,
  isInFrontOfCamera,
  linesSharingPoint,
  polygonArea,
  pointsClose,
  pointsEqual,
  rankCandidates,
  worldToScreen,
  type LineEndpointRef,
  type Point3,
  type SnapMeasurementLike,
  type VertexRef,
} from "./vertexDragGeometry";

export type { Point3, VertexRef } from "./vertexDragGeometry";
export { findNearestScreenPoint } from "./vertexDragGeometry";

export enum VertexDragEvent {
  DragStart = "vertex-drag-start",
  DragEnd = "vertex-drag-end",
  // The viewer selected a measurement itself (a click on its label); the panel adopts it as its selection.
  SelectionPicked = "vertex-drag-selection-picked",
}

// `related`: the other measurements moving with this point, e.g. the lines meeting at a chain junction.
export interface VertexDragStart extends VertexRef {
  related: string[];
}

export interface VertexDragEventPayload {
  [VertexDragEvent.DragStart]: VertexDragStart;
  [VertexDragEvent.DragEnd]: void;
  [VertexDragEvent.SelectionPicked]: string;
}

const GRAB_RADIUS_PX = 12;
// Screen-space radius for snapping a dragged corner onto another measurement point or a mesh vertex.
const SNAP_RADIUS_PX = 12;
// A press that stays within this is a click on the corner, not a drag, and must not nudge it.
const DRAG_THRESHOLD_PX = 4;

// A richer mesh raycast than viewer.query() — carries face/batchObject, which the SDK's own vertex
// snap needs. Reflectively calling that real snap() keeps the drag consistent with point placement.
const queryHit = (
  viewer: IViewer,
  measurements: MeasurementsExtension,
  camera: Camera,
  clientX: number,
  clientY: number,
  rect: DOMRect
): Point3 | null => {
  const ndc = toNdc(clientX, clientY, rect);
  const renderer = viewer.getRenderer();
  const hit = renderer.intersections.intersect(
    renderer.scene,
    camera,
    new Vector2(ndc.x, ndc.y),
    ObjectLayers.STREAM_CONTENT_MESH,
    true,
    renderer.clippingVolume
  )?.[0];
  if (!hit) return null;

  const outPoint = hit.point.clone();
  if (measurements.options.vertexSnap) {
    try {
      const outNormal = hit.face.normal.clone();
      (measurements as unknown as { snap: (intersection: typeof hit, point: Vector3, normal: Vector3) => void }).snap(
        hit,
        outPoint,
        outNormal
      );
    } catch {
      // Reflective access to the SDK's own protected snap(); if its shape ever changes, fall back to the raw hit.
    }
  }
  return [outPoint.x, outPoint.y, outPoint.z];
};

interface LiveAreaHandle {
  measurementId: string;
  measurementType: MeasurementType;
  points: Vector3[];
  polygonPoints: Vector3[];
  units: string;
  precision: number;
  labelPoint: Vector3;
  pointGizmos: {
    updatePoint: (v: Vector3) => void;
    updateLine: (points: Vector3[]) => void;
    updateText: (text: string, position?: Vector3) => unknown;
  }[];
  updateFillPolygon: (points: Vector3[]) => void;
  updateLabelLocation: (points: Vector3[]) => void;
}

// The label's glyph rebuild is the expensive part of a live update, so it runs at most this often.
const LABEL_INTERVAL_MS = 100;

// A line's own startPoint/endPoint stay untouched during a live drag (only the gizmos below move),
// so replaceLineVertex can always read the other, unmoved end back off the instance at commit time.
interface LiveLineHandle {
  measurementType: MeasurementType;
  startPoint: Vector3;
  endPoint: Vector3;
  startGizmo: { updatePoint: (v: Vector3) => void; updateLine: (points: Vector3[]) => void } | null;
  endGizmo: { updatePoint: (v: Vector3) => void } | null;
}

// The in-progress area as the SDK's hover handler sees it: `snap` runs before its own vertex snap.
interface DrawingAreaHandle {
  measurementType: MeasurementType;
  pointIndex: number;
  planeOrigin: Vector3;
  planeNormal: Vector3;
  snap: (ndc: { x: number; y: number }, hit: unknown, point: Vector3, normal: Vector3) => boolean;
}

// Where the SDK keeps the hover point it will place on the next click: areas buffer it, lines write it straight in.
interface HoverHandle {
  measurementType: MeasurementType;
  surfacePoint?: Vector3;
  startPoint?: Vector3;
}

const toPoint3 = (vector: Vector3): Point3 => [vector.x, vector.y, vector.z];
const raycaster = new Raycaster();

// The label repaints its pill from these params after every text update, so the accent has to live there too.
interface HighlightLabel {
  backgroundMesh?: { material?: { color?: Color } } | null;
  updateParams?: (params: { backgroundColor: Color }) => Promise<void>;
  _params?: { backgroundColor?: Color | null };
}

// The parts of a gizmo that take the accent: the SDK's `updateStyle` restores the first three, the label's
// pill is ours to put back, so its original colour is remembered per label.
interface HighlightGizmo {
  normalIndicator?: { material?: { color?: Color } };
  line?: { material?: { color?: Color } };
  point?: { material?: { color?: Color } };
  text?: HighlightLabel | null;
  updateStyle?: () => void;
}

const pillColors = new WeakMap<object, Color>();

// Materials hold linear colours and the params sRGB ones, which the label converts on its next `updateBackground`.
const paintPill = (label: HighlightLabel, srgb: Color) => {
  label.backgroundMesh?.material?.color?.copy(srgb.clone().convertSRGBToLinear());
  void label.updateParams?.({ backgroundColor: srgb.clone() });
};

interface HighlightHandle {
  pointGizmos?: HighlightGizmo[];
  startGizmo?: HighlightGizmo;
  endGizmo?: HighlightGizmo;
}

const HIGHLIGHT_FALLBACK = "#8954c9";
// The theme's accent (app-purple-600), read live so dark mode's retuned ramp applies in the scene too.
const highlightColor = (): Color => {
  const token = getComputedStyle(document.documentElement).getPropertyValue("--color-app-purple-600").trim();
  return new Color(/^#[0-9a-f]{6}$/i.test(token) ? token : HIGHLIGHT_FALLBACK);
};

// Runs once per Extension.onEarlyUpdate call rather than on a DOM event, so a viewer, camera, or SDK
// dependency subclass this instead of a Vue component — see the approved plan in .omc/plans/.
export class VertexDragExtension extends Extension {
  get inject() {
    return [CameraController, MeasurementsExtension];
  }

  private container: HTMLElement;
  private cursorClient: { x: number; y: number } | null = null;
  private dragging: VertexRef | null = null;
  private dragOrigin: Point3 | null = null;
  private dragPlane: Plane | null = null;
  // Other lines sharing the grabbed line's endpoint (a chain junction or a closed loop's close) — moved
  // in lockstep so the chain stays connected. Always empty while dragging an area corner.
  private coMoving: LineEndpointRef[] = [];
  private snapCandidates: { point: Point3; x: number; y: number }[] = [];
  private dragPointerId: number | null = null;
  private lastDraggedPoint: Point3 | null = null;
  private hiddenPreview: Measurement | null = null;
  private pressClient: { x: number; y: number } | null = null;
  private dragMoved = false;
  private replaying = false;
  private aligning = false;
  private constrainedAreas = new WeakSet<object>();
  // The panel's selected row: first in line for a shared-point grab, and highlighted until deselected.
  private selectedId: string | null = null;
  private selectionDirty = false;
  private hovering: string[] = [];
  private hoverChecked: { x: number; y: number } | null = null;
  // Set by the Measure panel, which owns the row numbers the model's labels carry.
  liveLabelText: ((measurementId: string, squareMetres: number, units: string, precision: number) => string | null) | null = null;
  private pendingLabel: { instance: LiveAreaHandle; text: string } | null = null;
  private lastLabelText: string | null = null;
  private lastLabelAt = 0;

  constructor(
    viewer: IViewer,
    private cameraProvider: CameraController,
    private measurements: MeasurementsExtension
  ) {
    super(viewer);
    this.container = this.viewer.getContainer();
    // Capture-phase, matching measurementPickAssist.ts's own reasoning: must see the gesture before
    // the SDK's own click handling and the camera's orbit-drag do.
    this.container.addEventListener("pointerdown", this.handlePointerDown, true);
    this.container.addEventListener("pointermove", this.handlePointerMove, true);
    this.container.addEventListener("pointerup", this.handlePointerUp, true);
    this.container.addEventListener("pointercancel", this.handlePointerCancel, true);
    this.container.addEventListener("lostpointercapture", this.handlePointerCancel, true);
    this.container.addEventListener("pointerleave", this.handlePointerLeave);
    this.measurements.on(MeasurementEvent.MeasurementsChanged, this.handleMeasurementsChanged);
  }

  // A viewer rebuilt on the same container would otherwise keep this one grabbing vertices underneath it.
  dispose() {
    this.resetDragState();
    this.clearHover();
    if (this.selectedId) this.applyHighlight([this.selectedId], false);
    this.container.removeEventListener("pointerdown", this.handlePointerDown, true);
    this.container.removeEventListener("pointermove", this.handlePointerMove, true);
    this.container.removeEventListener("pointerup", this.handlePointerUp, true);
    this.container.removeEventListener("pointercancel", this.handlePointerCancel, true);
    this.container.removeEventListener("lostpointercapture", this.handlePointerCancel, true);
    this.container.removeEventListener("pointerleave", this.handlePointerLeave);
    this.measurements.removeListener(MeasurementEvent.MeasurementsChanged, this.handleMeasurementsChanged);
  }

  // Deferred: this fires from inside the SDK's own add/remove, which the rebuild below would re-enter.
  private handleMeasurementsChanged = () => {
    this.selectionDirty = true;
    queueMicrotask(() => this.alignAllAreas());
  };

  private alignAllAreas() {
    if (this.aligning || this.dragging) return;
    this.aligning = true;
    try {
      for (const measurement of [...this.measurements.mesurements]) {
        if (measurement.measurementType !== MeasurementType.AREA || measurement.state !== MeasurementState.COMPLETE) continue;
        const data = measurement.toMeasurementData();
        const aligned = alignAreaToPlane(data.innerPoints ?? [], data.startPoint, data.startNormal);
        if (aligned) this.rebuildMeasurement({ ...data, ...aligned }, data);
      }
    } finally {
      this.aligning = false;
    }
  }

  on<T extends VertexDragEvent>(eventType: T, listener: (arg: VertexDragEventPayload[T]) => void) {
    super.on(eventType, listener as (...args: unknown[]) => void);
  }

  // Extension.enabled defaults to false — the Measure tool's own active watcher turns this on, the
  // same way it toggles measureRef/selectRef, so a vertex is only grabbable while that tool is open.
  get enabled(): boolean {
    return super.enabled;
  }

  set enabled(value: boolean) {
    super.enabled = value;
    if (!value) {
      this.finishDrag(true);
      this.clearHover();
      this.setSelected(null);
    }
  }

  // Selection is owned by the panel; it decides which measurement a shared point moves and stays highlighted.
  setSelected(measurementId: string | null) {
    const previous = this.selectedId;
    this.selectedId = measurementId;
    if (previous && previous !== measurementId && !this.hovering.includes(previous) && !this.dragIds().includes(previous)) {
      this.applyHighlight([previous], false);
    }
    if (measurementId) this.applyHighlight([measurementId], true);
  }

  // Puts the dragged point back where it started, e.g. on Escape.
  cancelDrag() {
    this.finishDrag(false);
  }

  // Every completed area's own vertices, plus every complete line's two endpoints — an in-progress one's
  // points are excluded, or grabbing one would steal the pointerdown its own click-to-place needs.
  private draggableVertices(): (VertexRef & { point: Point3; kind: MeasurementType })[] {
    const result: (VertexRef & { point: Point3; kind: MeasurementType })[] = [];
    for (const measurement of this.measurements.mesurements) {
      if (measurement.state !== MeasurementState.COMPLETE) continue;
      if (measurement.measurementType === MeasurementType.AREA) {
        const innerPoints = measurement.toMeasurementData().innerPoints ?? [];
        const closed = innerPoints.length > 1 && pointsEqual(innerPoints[0], innerPoints[innerPoints.length - 1]);
        const vertexCount = closed ? innerPoints.length - 1 : innerPoints.length;
        for (let index = 0; index < vertexCount; index++) {
          result.push({ measurementId: measurement.measurementId, index, point: innerPoints[index], kind: MeasurementType.AREA });
        }
      } else if (measurement.measurementType === MeasurementType.POINTTOPOINT) {
        const data = measurement.toMeasurementData();
        const kind = MeasurementType.POINTTOPOINT;
        result.push({ measurementId: measurement.measurementId, index: 0, point: data.startPoint, kind });
        result.push({ measurementId: measurement.measurementId, index: 1, point: data.endPoint, kind });
      }
    }
    return result;
  }

  private findMeasurement(measurementId: string): Measurement | undefined {
    return this.measurements.mesurements.find((entry) => entry.measurementId === measurementId);
  }

  // Every other complete line's own endpoints, reshaped for linesSharingPoint.
  private lineSources(): { measurementId: string; startPoint: Point3; endPoint: Point3 }[] {
    return this.measurements.mesurements
      .filter(
        (measurement) =>
          measurement.measurementType === MeasurementType.POINTTOPOINT && measurement.state === MeasurementState.COMPLETE
      )
      .map((measurement) => {
        const data = measurement.toMeasurementData();
        return { measurementId: measurement.measurementId, startPoint: data.startPoint, endPoint: data.endPoint };
      });
  }

  // Complete measurements' own data, reshaped for collectSnapPoints — keeps that module free of the SDK type.
  private snapSources(): SnapMeasurementLike[] {
    return this.measurements.mesurements
      .filter((measurement) => measurement.state === MeasurementState.COMPLETE)
      .map((measurement) => {
        const data = measurement.toMeasurementData();
        return {
          measurementId: measurement.measurementId,
          type: data.type,
          innerPoints: data.innerPoints,
          startPoint: data.startPoint,
          endPoint: data.endPoint,
        };
      });
  }

  private replaceVertex(measurementId: string, index: number, newPoint: Point3): boolean {
    const data = this.findMeasurement(measurementId)?.toMeasurementData();
    if (!data) return false;
    if (data.type === MeasurementType.AREA) return this.replaceAreaVertex(data, index, newPoint);
    if (data.type === MeasurementType.POINTTOPOINT && (index === 0 || index === 1)) {
      return this.replaceLineVertex(data, index, newPoint);
    }
    return false;
  }

  private replaceAreaVertex(data: MeasurementData, index: number, newPoint: Point3): boolean {
    const innerPoints = data.innerPoints ?? [];
    const closed = innerPoints.length > 1 && pointsEqual(innerPoints[0], innerPoints[innerPoints.length - 1]);
    const vertexCount = closed ? innerPoints.length - 1 : innerPoints.length;
    if (index >= vertexCount) return false;

    const moved = innerPoints.map((point, i) => {
      if (i === index) return newPoint;
      // The closing point mirrors the first one, so moving point 1 moves it too, or the loop tears.
      if (closed && index === 0 && i === innerPoints.length - 1) return newPoint;
      return point;
    });
    const startPoint = index === 0 ? newPoint : data.startPoint;
    const next = { ...data, innerPoints: moved, startPoint };
    return this.rebuildMeasurement({ ...next, ...alignAreaToPlane(moved, startPoint, data.startNormal) }, data);
  }

  // The moved end's counterpart comes straight off `data` (read fresh, at commit time, from the
  // instance's own untouched startPoint/endPoint — see LiveLineHandle), so it is never stale.
  private replaceLineVertex(data: MeasurementData, index: 0 | 1, newPoint: Point3): boolean {
    const next = index === 0 ? { ...data, startPoint: newPoint } : { ...data, endPoint: newPoint };
    return this.rebuildMeasurement(next, data);
  }

  // Falls back to `previous` when the SDK drops the rebuild, which it silently does for a zero area.
  private rebuildMeasurement(data: MeasurementData, previous: MeasurementData): boolean {
    // The SDK would drop a zero area (logging an error), so leave the area as it was instead.
    if (data.type === MeasurementType.AREA && polygonArea(data.innerPoints ?? []) < 1e-9) return false;
    // addMeasurement overwrites type/chain/vertexSnap and, aliasing its own options object, never restores them.
    const options = { ...this.measurements.options };
    const list = this.measurements.mesurements;
    const position = list.findIndex((entry) => entry.measurementId === data.uuid);
    this.measurements.removeMeasurement(data);
    this.measurements.addMeasurement(data);
    const dropped = !list.some((entry) => entry.measurementId === data.uuid);
    if (dropped) this.measurements.addMeasurement(previous);
    this.measurements.options = options;
    // addMeasurement appends; put the measurement back where it was, since chain detection reads the list's tail.
    const added = list.findIndex((entry) => entry.measurementId === data.uuid);
    if (position >= 0 && added >= 0 && added !== position) {
      list.splice(position, 0, ...list.splice(added, 1));
      this.measurements.emit(MeasurementEvent.MeasurementsChanged, list);
    }
    return !dropped;
  }

  // Mid-shape, a click on another area's corner is the next point of the new shape (snapping onto it).
  isDrawing(): boolean {
    const active = this.measurements.activeMeasurement;
    if (!active) return false;
    if (active.state === MeasurementState.DANGLING_END) return true;
    return ((active as unknown as { points?: unknown[] }).points?.length ?? 0) > 0;
  }

  // replaceVertex rebuilds gizmos/labels/fill from scratch — too expensive for every frame. This moves
  // the live SDK objects directly, the same way the SDK's own in-progress drawing does. Area corners only.
  private moveGizmoLive(measurementId: string, index: number, point: Point3): boolean {
    try {
      const instance = this.findMeasurement(measurementId) as unknown as LiveAreaHandle | undefined;
      const points = instance?.points;
      const gizmos = instance?.pointGizmos;
      if (instance?.measurementType !== MeasurementType.AREA || !points?.length || !gizmos?.[index]) return false;

      const vec = new Vector3(point[0], point[1], point[2]);
      const last = points.length - 1;
      // Gizmo i draws the edge from point i to point i+1; a closed area's last point duplicates point 0.
      const closed = last > 0 && points[0].distanceTo(points[last]) < 1e-9;
      const prevIndex = index > 0 ? index - 1 : closed ? last - 1 : last;
      gizmos[index].updatePoint(vec);
      if (closed && index === 0) gizmos[last]?.updatePoint(vec);
      gizmos[index].updateLine([vec, points[index + 1] ?? points[0]]);
      gizmos[prevIndex]?.updateLine([points[prevIndex], vec]);
      this.moveFillLive(instance, index, point);
      return true;
    } catch {
      // Reaching into the SDK's own internal gizmo shape; if it ever changes, fall back to the
      // slower but always-correct full commit below rather than leaving the drag broken.
      return false;
    }
  }

  // polygonPoints is [a copy of the last point, then every point flattened onto the area's plane].
  // The dragged point is already on that plane (dragPoint), so it goes in as-is.
  private moveFillLive(instance: LiveAreaHandle, index: number, point: Point3) {
    try {
      const { points, polygonPoints } = instance;
      if (polygonPoints.length !== points.length + 1) return;
      const last = points.length - 1;
      const closed = last > 0 && points[0].distanceTo(points[last]) < 1e-9;
      const fill = polygonPoints.map((vector) => vector.clone());
      fill[index + 1] = new Vector3(point[0], point[1], point[2]);
      if (closed && index === 0) fill[last + 1] = fill[1];
      fill[0] = fill[last + 1];
      instance.updateFillPolygon(fill);
      instance.updateLabelLocation(fill.slice(1));
      this.queueLabel(instance, polygonArea(fill.slice(1).map(toPoint3)));
    } catch {
      // intentional: the fill and label then just catch up on release, rather than a full rebuild every frame
    }
  }

  private queueLabel(instance: LiveAreaHandle, squareMetres: number) {
    const text = this.liveLabelText?.(instance.measurementId, squareMetres, instance.units, instance.precision);
    if (text && text !== this.lastLabelText) this.pendingLabel = { instance, text };
  }

  private flushLabel() {
    const pending = this.pendingLabel;
    if (!pending || performance.now() - this.lastLabelAt < LABEL_INTERVAL_MS) return;
    this.pendingLabel = null;
    this.lastLabelText = pending.text;
    this.lastLabelAt = performance.now();
    try {
      void Promise.resolve(pending.instance.pointGizmos[0].updateText(pending.text, pending.instance.labelPoint)).then(() =>
        this.viewer.requestRender()
      );
    } catch {
      // intentional: the number then just updates on release, as the full rebuild rewrites it
    }
  }

  // The SDK's own vertex snap, run on copies: the corner it would snap to, or null when it wouldn't snap.
  private vertexSnapOf(hit: unknown, point: Vector3, normal: Vector3): Vector3 | null {
    if (!hit || !this.measurements.options.vertexSnap) return null;
    const snapped = point.clone();
    (this.measurements as unknown as { snap: (intersection: unknown, point: Vector3, normal: Vector3) => void }).snap(
      hit,
      snapped,
      normal.clone()
    );
    return snapped.distanceTo(point) > 1e-9 ? snapped : null;
  }

  // Attached once per in-progress area; the SDK's own close-on-first-point snap still runs first and wins.
  private attachAreaConstraint() {
    const active = this.measurements.activeMeasurement as unknown as DrawingAreaHandle | null;
    if (!active || active.measurementType !== MeasurementType.AREA || this.constrainedAreas.has(active)) return;
    if (typeof active.snap !== "function") return;
    this.constrainedAreas.add(active);
    const original = active.snap.bind(active);
    active.snap = (ndc, hit, point, normal) => {
      if (original(ndc, hit, point, normal)) return true;
      try {
        const camera = this.cameraProvider.renderingCamera;
        if (active.pointIndex < 1 || !camera) return false;
        raycaster.setFromCamera(new Vector2(ndc.x, ndc.y), camera);
        const result = constrainToAreaPlane({
          hitPoint: toPoint3(point),
          hitNormal: toPoint3(normal),
          rayOrigin: toPoint3(raycaster.ray.origin),
          rayDirection: toPoint3(raycaster.ray.direction),
          planeOrigin: toPoint3(active.planeOrigin),
          planeNormal: toPoint3(active.planeNormal),
        });
        if (!result.constrained) return false;
        const vertex = this.vertexSnapOf(hit, point, normal);
        if (vertex && isOnAreaSurface(toPoint3(vertex), toPoint3(active.planeOrigin), toPoint3(active.planeNormal))) {
          point.copy(vertex);
          normal.copy(active.planeNormal);
          return true;
        }
        point.set(...result.point);
        normal.set(...result.normal);
        return true;
      } catch {
        // Reflective use of the SDK's hover hook; if its shape changes, drawing falls back to the raw hit.
        return false;
      }
    };
  }

  // Mirrors the SDK's own PointToPointMeasurement.update() in its COMPLETE state: startGizmo draws the
  // line and carries the label, endGizmo only draws its own point. Label text/position wait for release.
  private moveLineGizmoLive(measurementId: string, index: number, point: Point3): boolean {
    if (index !== 0 && index !== 1) return false;
    try {
      const instance = this.findMeasurement(measurementId) as unknown as LiveLineHandle | undefined;
      if (instance?.measurementType !== MeasurementType.POINTTOPOINT || !instance.startGizmo || !instance.endGizmo) return false;

      const vec = new Vector3(point[0], point[1], point[2]);
      const start = index === 0 ? vec : instance.startPoint;
      const end = index === 1 ? vec : instance.endPoint;
      instance.startGizmo.updatePoint(start);
      instance.endGizmo.updatePoint(end);
      instance.startGizmo.updateLine([start, end]);
      return true;
    } catch {
      // Reaching into the SDK's own internal gizmo shape; if it ever changes, fall back to the
      // slower but always-correct full commit below rather than leaving the drag broken.
      return false;
    }
  }

  // An area lives on one plane and the SDK flattens its fill onto it, so a point dragged off that plane
  // left the fill and outline apart. Keep a surface/snap hit only while it stays under the cursor on the plane.
  private dragPoint(
    camera: PerspectiveCamera | OrthographicCamera,
    clientX: number,
    clientY: number,
    rect: DOMRect
  ): Point3 | null {
    const plane = this.dragPlane;
    const project = (point: Point3) =>
      plane ? toPoint3(plane.projectPoint(new Vector3(point[0], point[1], point[2]), new Vector3())) : point;

    // Highest priority: another measurement's own point, judged purely by screen distance to the cursor.
    const snapped = findNearestScreenPoint(this.snapCandidates, clientX, clientY, SNAP_RADIUS_PX);
    if (snapped) return project(snapped.point);

    const surface = queryHit(this.viewer, this.measurements, camera, clientX, clientY, rect);
    if (!plane) return surface;

    const ndc = toNdc(clientX, clientY, rect);
    raycaster.setFromCamera(new Vector2(ndc.x, ndc.y), camera);
    const onPlane = raycaster.ray.intersectPlane(plane, new Vector3());
    if (surface) {
      // vertexSnap on: `surface` is the SDK's own snapped mesh vertex — accept only near the cursor.
      // vertexSnap off: it's a raw face point under the cursor, always near it — same as before.
      const surfaceScreen = worldToScreen(surface, camera, rect);
      if (Math.hypot(surfaceScreen.x - clientX, surfaceScreen.y - clientY) <= SNAP_RADIUS_PX) return project(surface);
    }
    return onPlane ? toPoint3(onPlane) : null;
  }

  private planeOf(measurementId: string): Plane | null {
    const data = this.findMeasurement(measurementId)?.toMeasurementData();
    if (!data) return null;
    const normal = new Vector3(...data.startNormal);
    if (normal.lengthSq() < 1e-12) return null;
    return new Plane().setFromNormalAndCoplanarPoint(normal.normalize(), new Vector3(...data.startPoint));
  }

  // Corners behind the camera or cut away by the section box aren't drawn, so they can't be grabbed.
  private isVisible(point: Point3, camera: Camera): boolean {
    if (!isInFrontOfCamera(point, camera)) return false;
    const vector = new Vector3(point[0], point[1], point[2]);
    return this.viewer.getRenderer().clippingPlanes.every((plane) => plane.distanceToPoint(vector) >= 0);
  }

  // The point a press here would grab: nearest within the radius, ties going to the selection, then the mode.
  private grabTargetAt(clientX: number, clientY: number, camera: Camera, rect: DOMRect) {
    const candidates = rankCandidates(
      this.draggableVertices()
        .filter((vertex) => this.isVisible(vertex.point, camera))
        .map((vertex) => ({ ...vertex, ...worldToScreen(vertex.point, camera, rect) })),
      { selectedId: this.selectedId, kind: this.measurements.options.type }
    );
    return findNearestScreenPoint(candidates, clientX, clientY, GRAB_RADIUS_PX);
  }

  private linesJoinedAt(grabbed: { measurementId: string; point: Point3; kind: MeasurementType }): LineEndpointRef[] {
    if (grabbed.kind !== MeasurementType.POINTTOPOINT) return [];
    return linesSharingPoint(this.lineSources(), grabbed.point, grabbed.measurementId);
  }

  private handlePointerDown = (event: PointerEvent) => {
    // A second pointer (extra finger, pen, synthetic) must not restart or overwrite an active drag.
    if (!this.enabled || this.replaying || event.button !== 0 || this.isDrawing() || this.dragging) return;
    const camera = this.cameraProvider.renderingCamera;
    if (!camera) return;

    const rect = this.container.getBoundingClientRect();
    const grabbed = this.grabTargetAt(event.clientX, event.clientY, camera, rect);
    if (!grabbed) return;

    const grabbedType = grabbed.kind;
    this.dragging = { measurementId: grabbed.measurementId, index: grabbed.index };
    this.dragOrigin = grabbed.point;
    // A line endpoint lives on geometry, not a fitted plane — dragPoint falls through to the raw mesh hit.
    this.dragPlane = grabbedType === MeasurementType.AREA ? this.planeOf(grabbed.measurementId) : null;
    this.coMoving = this.linesJoinedAt(grabbed);
    this.hovering = [];
    this.container.style.cursor = "grabbing";
    // A junction's twin point sits exactly under the cursor at drag start — offering it back as a snap
    // target would pin the drag to its own origin, so it's dropped before the rest become candidates.
    this.snapCandidates = collectSnapPoints(this.snapSources(), this.dragging)
      .filter((point) => !pointsClose(point, grabbed.point))
      .map((point) => ({ point, ...worldToScreen(point, camera, rect) }));
    this.dragPointerId = event.pointerId;
    this.cursorClient = { x: event.clientX, y: event.clientY };
    this.pressClient = { x: event.clientX, y: event.clientY };
    this.dragMoved = false;
    this.lastDraggedPoint = null;
    event.stopImmediatePropagation();
    event.preventDefault();
    // Keeps pointermove/pointerup coming here when the release happens outside the viewer.
    try {
      this.container.setPointerCapture(event.pointerId);
    } catch {
      // intentional: an untrusted (synthetic) pointer can't be captured; the drag still works without it
    }

    this.cameraProvider.enabled = false;
    // The SDK's "next point" preview froze where the press started, since this drag swallows its moves.
    this.hiddenPreview = this.measurements.activeMeasurement;
    if (this.hiddenPreview) this.hiddenPreview.isVisible = false;
    // Pick-assist corrects hover precision for the SDK's own click-to-place flow, which isn't in play
    // while dragging an existing vertex — it was raycasting in parallel with this drag for no reason.
    uninstallPickAssist();
    this.applyHighlight(this.dragIds(), true);
    this.emit(VertexDragEvent.DragStart, { ...this.dragging, related: this.coMoving.map((co) => co.measurementId) });
  };

  private handlePointerMove = (event: PointerEvent) => {
    if (!this.dragging) {
      // Only recorded here; onEarlyUpdate turns it into the hover preview once per frame.
      if (this.enabled && !this.replaying) this.cursorClient = { x: event.clientX, y: event.clientY };
      return;
    }
    if (event.pointerId !== this.dragPointerId) return;
    // Suppress on every move while dragging — otherwise a move the extension itself doesn't otherwise
    // act on (it only records cursor position here) reaches the camera's orbit-drag handling underneath.
    event.stopImmediatePropagation();
    event.preventDefault();
    this.cursorClient = { x: event.clientX, y: event.clientY };
    const press = this.pressClient;
    if (!this.dragMoved && press && Math.hypot(event.clientX - press.x, event.clientY - press.y) >= DRAG_THRESHOLD_PX) {
      this.dragMoved = true;
    }
  };

  // The panel sits outside the viewer, so a preview lit before the cursor left must not stay red.
  private handlePointerLeave = () => {
    if (this.dragging) return;
    this.clearHover();
    this.cursorClient = null;
  };

  private handlePointerUp = (event: PointerEvent) => {
    if (!this.dragging) {
      // The SDK's click handler runs after this and clears every highlight; the selection is put back next frame.
      if (!this.replaying) this.selectionDirty = true;
      return;
    }
    if (event.pointerId !== this.dragPointerId) return;
    event.stopImmediatePropagation();
    event.preventDefault();
    this.finishDrag(true);
  };

  private handlePointerCancel = (event: PointerEvent) => {
    if (!this.dragging || event.pointerId !== this.dragPointerId) return;
    this.finishDrag(false);
  };

  // Shared by finishDrag and dispose() — releases capture, restores camera/preview, clears drag fields.
  // No rebuild or emit here: dispose() may run after the measurements extension itself is gone.
  private resetDragState() {
    this.applyHighlight(
      this.dragIds().filter((id) => id !== this.selectedId),
      false
    );
    this.container.style.cursor = "";
    this.hoverChecked = null;
    const pointerId = this.dragPointerId;
    try {
      if (pointerId !== null && this.container.hasPointerCapture(pointerId)) this.container.releasePointerCapture(pointerId);
    } catch {
      // intentional: the container may already be detached, or never actually captured this pointer
    }
    if (this.hiddenPreview && this.hiddenPreview === this.measurements.activeMeasurement) this.hiddenPreview.isVisible = true;
    this.hiddenPreview = null;
    if (this.cameraProvider) this.cameraProvider.enabled = true;
    this.dragging = null;
    this.dragOrigin = null;
    this.dragPlane = null;
    this.coMoving = [];
    this.snapCandidates = [];
    this.dragPointerId = null;
    this.cursorClient = null;
    this.pressClient = null;
    this.dragMoved = false;
    this.lastDraggedPoint = null;
    this.pendingLabel = null;
    this.lastLabelText = null;
    this.lastLabelAt = 0;
  }

  // Shared by release, cancel (Escape, pointercancel, lost capture) and the tool closing mid-drag.
  // Only a live drag has moved the gizmos, so only then is a rebuild needed, to the new or the original point.
  private finishDrag(commit: boolean) {
    const dragging = this.dragging;
    if (!dragging) return;

    const moved = this.lastDraggedPoint;
    // A junction dropped on a line's other end would zero that line, which the SDK drops: revert the whole drag instead.
    const target = commit && moved && !this.wouldCollapse(moved) ? moved : this.dragOrigin;
    const clickedInPlace = commit && !moved;
    const press = this.pressClient;
    const origin = this.dragOrigin;
    if (moved && target) {
      this.replaceVertex(dragging.measurementId, dragging.index, target);
      // The shared point is `target` either way: on commit it moved there, on cancel it never left.
      for (const co of this.coMoving) this.replaceVertex(co.measurementId, co.index, target);
      this.viewer.requestRender();
    }
    this.resetDragState();
    // The Measure tool removes pick-assist itself before disabling this extension; don't put it back then.
    if (this.enabled) installPickAssist(this.viewer);
    this.emit(VertexDragEvent.DragEnd, undefined);
    if (clickedInPlace && press && origin && this.enabled) this.placePointAt(origin, press);
  }

  private dragIds(): string[] {
    return this.dragging ? [this.dragging.measurementId, ...this.coMoving.map((co) => co.measurementId)] : [];
  }

  // One accent for the selection, the hover preview and the drag alike, on the parts the SDK's highlight uses.
  private applyHighlight(ids: string[], on: boolean) {
    for (const id of ids) {
      try {
        this.paintHighlight(this.findMeasurement(id) as unknown as HighlightHandle | undefined, on);
      } catch {
        // intentional: private SDK surface; everything works without the highlight
      }
    }
    if (ids.length) this.viewer.requestRender();
  }

  private paintHighlight(handle: HighlightHandle | undefined, on: boolean) {
    const gizmos = [...(handle?.pointGizmos ?? []), handle?.startGizmo, handle?.endGizmo].filter(
      (gizmo): gizmo is HighlightGizmo => !!gizmo
    );
    const color = on ? highlightColor() : null;
    const linear = color?.clone().convertSRGBToLinear();
    for (const gizmo of gizmos) {
      const label = gizmo.text;
      if (!color || !linear) {
        gizmo.updateStyle?.();
        const original = label ? pillColors.get(label) : undefined;
        if (label && original) paintPill(label, original);
        continue;
      }
      for (const part of [gizmo.normalIndicator, gizmo.line, gizmo.point])
        if (part?.material) part.material.color = linear.clone();
      if (label) {
        const base = label._params?.backgroundColor ?? label.backgroundMesh?.material?.color?.clone().convertLinearToSRGB();
        if (base && !pillColors.has(label)) pillColors.set(label, base.clone());
        paintPill(label, color);
      }
    }
  }

  private clearHover() {
    this.applyHighlight(
      this.hovering.filter((id) => id !== this.selectedId),
      false
    );
    this.hovering = [];
    this.hoverChecked = null;
    if (!this.dragging) this.container.style.cursor = "";
  }

  // Shows what a press would move before it happens: the grabbable point's measurement and its chain twins.
  private updateHover(camera: Camera, cursor: { x: number; y: number }) {
    if (this.hoverChecked && this.hoverChecked.x === cursor.x && this.hoverChecked.y === cursor.y) return;
    this.hoverChecked = { ...cursor };
    const rect = this.container.getBoundingClientRect();
    const grabbed = this.isDrawing() ? null : this.grabTargetAt(cursor.x, cursor.y, camera, rect);
    const ids = grabbed ? [grabbed.measurementId, ...this.linesJoinedAt(grabbed).map((co) => co.measurementId)] : [];
    if (ids.length === this.hovering.length && ids.every((id, index) => id === this.hovering[index])) return;
    this.applyHighlight(
      this.hovering.filter((id) => id !== this.selectedId && !ids.includes(id)),
      false
    );
    this.applyHighlight(ids, true);
    this.hovering = ids;
    this.container.style.cursor = ids.length ? "grab" : "";
  }

  // The SDK clears every highlight on its own clicks and selects a measurement when its label is clicked.
  private syncSelection() {
    if (!this.selectionDirty) return;
    this.selectionDirty = false;
    try {
      const sdk = this.measurements as unknown as { _selectedMeasurement?: { measurementId?: string } | null };
      const picked = sdk._selectedMeasurement?.measurementId ?? null;
      if (picked) {
        sdk._selectedMeasurement = null;
        if (picked !== this.selectedId) this.emit(VertexDragEvent.SelectionPicked, picked);
      }
    } catch {
      // intentional: private SDK field; a label click then simply doesn't select
    }
    if (this.selectedId) this.applyHighlight([this.selectedId], true);
    if (this.hovering.length) this.applyHighlight(this.hovering, true);
  }

  private wouldCollapse(target: Point3): boolean {
    const refs = this.dragging ? [this.dragging, ...this.coMoving] : this.coMoving;
    return refs.some((ref) => {
      const data = this.findMeasurement(ref.measurementId)?.toMeasurementData();
      if (data?.type !== MeasurementType.POINTTOPOINT) return false;
      return pointsClose(ref.index === 0 ? data.endPoint : data.startPoint, target);
    });
  }

  // The grab swallowed the SDK's pointerdown, so a click that never became a drag is handed back to it here:
  // a new line or area then starts exactly on the existing point, which is how a chain is built.
  private placePointAt(vertex: Point3, client: { x: number; y: number }) {
    try {
      const active = this.measurements.activeMeasurement as unknown as HoverHandle | null;
      const hover = active?.measurementType === MeasurementType.AREA ? active.surfacePoint : active?.startPoint;
      hover?.set(vertex[0], vertex[1], vertex[2]);
    } catch {
      // intentional: the click then lands on the SDK's own hover point, a raw hit a few centimetres off
    }
    // The SDK's input listens on the canvas, not the container; an event dispatched on the container never reaches it.
    const canvas = this.viewer.getRenderer().renderer.domElement ?? this.container;
    this.replaying = true;
    try {
      for (const type of ["pointerdown", "pointerup"] as const) {
        canvas.dispatchEvent(
          new PointerEvent(type, {
            bubbles: true,
            cancelable: true,
            pointerType: "mouse",
            isPrimary: true,
            button: 0,
            buttons: type === "pointerdown" ? 1 : 0,
            clientX: client.x,
            clientY: client.y,
          })
        );
      }
    } finally {
      this.replaying = false;
    }
  }

  // The SDK's own per-frame hook — runs once per real render frame, not approximated by a JS timer.
  onEarlyUpdate() {
    // Before the early return, so the last pending number still lands once the cursor stops.
    this.flushLabel();
    if (this.enabled) this.attachAreaConstraint();
    const dragging = this.dragging;
    const cursorClient = this.cursorClient;
    const camera = this.cameraProvider.renderingCamera;
    if (this.enabled && !dragging && camera) {
      this.syncSelection();
      if (cursorClient) this.updateHover(camera, cursorClient);
    }
    if (!dragging || !cursorClient || !camera || !this.dragMoved) return;

    const rect = this.container.getBoundingClientRect();
    const point = this.dragPoint(camera, cursorClient.x, cursorClient.y, rect);
    if (!point) return;
    this.lastDraggedPoint = point;

    const moved =
      this.moveGizmoLive(dragging.measurementId, dragging.index, point) ||
      this.moveLineGizmoLive(dragging.measurementId, dragging.index, point);
    if (!moved) this.replaceVertex(dragging.measurementId, dragging.index, point);
    for (const co of this.coMoving) {
      if (!this.moveLineGizmoLive(co.measurementId, co.index, point)) this.replaceVertex(co.measurementId, co.index, point);
    }
    this.viewer.requestRender();
  }
}
